import {
  CollectionItemsConnectionFragment,
  Item,
  ItemDetailBoxFragment,
  Maybe,
  MyOwnedTicketDetailQuery,
  PendingPurchaseItemDetailFragment,
  PortfolioConnectionFragment,
  PortfolioItemsConnectionFragment,
  SkuUnit,
  StoreCardDetailFragment,
  StoreItemCompleteDetailFragment,
  StoreItemDetailFragment,
  StorePurchaseHistoryFragment,
  UserCartFragment,
  UserCartTransactionOrderDetailFragment,
  UserCartTransactionOrderFragment,
  UserItemDetailBoxFragment,
  UserOwnedItemsConnectionFragment,
} from '@/clients/utoniq-core/schema'

/**
 * アイテムオブジェクトから指定したskuIdに一致するSKUを取得し、 sku プロパティに設定する。
 * サムネイルも変換する
 * @param item アイテム
 * @param skuId skuのユニットID。おもにクエリパラメータから取得する
 */
export const setItemSku = <
  T extends
    | Item
    | ItemDetailBoxFragment
    | StoreItemDetailFragment
    | StoreItemCompleteDetailFragment
    | undefined
    | null
>(
  item: T,
  skuId?: string | null | undefined,
  i?: UserItemDetailBoxFragment
) => {
  if (!item || !skuId || !item.skuSettings) return item

  // skuIdに一致するunitを探す
  const sku = item.skuSettings.units.find(
    unit => unit.id === decodeSkuId(skuId)
  )

  if (!sku)
    return {
      ...item,
      thumbnail: {
        ...item.thumbnail,
        front: {
          ...item.thumbnail?.front,
          ...item.sku?.thumbnail,
        },
      },
    } as T

  return {
    ...item,
    sku,
    thumbnail: {
      ...item.thumbnail,
      front: {
        ...item.thumbnail?.front,
        ...sku?.thumbnail,
      },
    },
  } as T
}

/**
 * ユーザー所持アイテム一覧のうち、SKUが設定されているものがあればSKU単位で展開する。
 * portfolios: {
 *   edges: [item, item, item, SKUありのitem, ]
 * }
 * を
 * portfolios: {
 *   edges: [item, item, item, item1, item2, item3 ]
 * }
 * にする
 */

export const spreadPortfoliosBySku = <
  T extends PortfolioItemsConnectionFragment | UserOwnedItemsConnectionFragment
>(
  portfolios: T | null | undefined
) => {
  if (!portfolios) return null

  const edges = portfolios.edges
    ?.flatMap(({ node }) => {
      if (!node?.item?.skuSettings?.units?.length) return { node }

      return node.item.skuSettings.units.map(unit => ({
        node: {
          ...node,
          number: node.serials.filter(
            ({ serial }) => serial?.sku?.id === unit.id
          ).length,
          serials:
            node.serials?.filter(({ serial }) => serial?.sku?.id === unit.id) ??
            node.serials,
          item: {
            ...node.item,
            sku: unit,
            thumbnail: {
              ...node.item.thumbnail,
              front: {
                ...node.item.thumbnail.front,
                ...unit.thumbnail,
              },
            },
          },
        },
      })) as T['edges']
    })
    // @ts-ignore
    .filter(m => !!m.node.number)

  return { ...portfolios, edges }
}

/**
 * コレクション一覧のうち、SKUが設定されているものがあればSKU単位で展開する。
 * collectionItems: {
 *   edges: [item, item, item, SKUありのitem, ]
 * }
 * を
 * collectionItems: {
 *   edges: [item, item, item, item1, item2, item3 ]
 * }
 * にする
 */

export const spreadCollectionItemsBySku = <
  T extends CollectionItemsConnectionFragment
>(
  collectionItems: T | null | undefined
) => {
  if (!collectionItems) return null

  const edges = collectionItems.edges?.flatMap(({ node }) => {
    if (!node?.item?.skuSettings?.units?.length) return { node }
    return node.item.skuSettings.units.map(unit => ({
      node: {
        ...node,
        item: {
          ...node.item,
          sku: unit,
          thumbnail: {
            ...node.item.thumbnail,
            front: {
              ...node.item.thumbnail.front,
              ...unit.thumbnail,
            },
          },
        },
      },
    })) as T['edges']
  })

  return { ...collectionItems, edges }
}

/**
 * ユーザーの所持アイテム(詳細)のパラメータをSKUのものに変換する
 */
export const convertPortfolioItemToSku = <
  T extends
    | PortfolioConnectionFragment
    | UserItemDetailBoxFragment
    | null
    | undefined
>(
  portfolio: T,
  skuId?: string
) => {
  if (!portfolio) return null
  if (!skuId) return portfolio

  return {
    ...portfolio,
    number: portfolio?.serials?.filter(
      ({ serial }) => serial?.sku?.id === decodeSkuId(skuId)
    ).length,
    serials:
      portfolio?.serials?.filter(
        ({ serial }) => serial?.sku?.id === decodeSkuId(skuId)
      ) ?? portfolio.serials,
    item: setItemSku(portfolio.item, skuId),
  } as T
}

/**
 * カートの中身のアイテムをSKU単位で並べ、アイテムの合計金額を算出する
 */
export const convertUserCartItemsToSku = (
  items:
    | UserCartFragment['items']
    | StoreCardDetailFragment['items']
    | UserCartTransactionOrderFragment['items']
    | UserCartTransactionOrderDetailFragment['items']
    | PendingPurchaseItemDetailFragment['items']
    | StorePurchaseHistoryFragment['items']
    | null
) => {
  if (!items) return null

  return items.flatMap(item => {
    if (!item.skus?.length) {
      return item
    }

    return item.skus.map(sku => {
      if (!sku) return item

      return {
        ...item,
        totalPrice: item.item.price * sku.count,
        count: sku.count,
        item: {
          ...item.item,
          sku: sku.sku,
          thumbnail: {
            ...item.item.thumbnail,
            front: {
              ...item.item.thumbnail.front,
              ...sku?.sku?.thumbnail,
            },
          },
        },
      }
    })
  })
}

/** クエリパラメータに設定するために、skuIdのコロンをハイフンに変換する */
export const encodeSkuId = (skuId: string) => skuId.replace(':', '-')
/** クエリパラメータからskuIdを取得するため、ハイフンをコロンに戻す */
export const decodeSkuId = (skuId: string) => skuId.replace('-', ':')

/**
 * userItems型の配列をsku単位で展開する
 */
export const spreadUserItemsBySku = (
  userItems: MyOwnedTicketDetailQuery['pickupUser']['userItems']
) => {
  if (!userItems || !userItems.length) return []

  return userItems.flatMap(userItem => {
    if (!userItem.item.skuSettings) return userItem

    return userItem.item.skuSettings.units
      .map(unit => ({
        ...userItem,
        number: userItem.serials.filter(
          ({ serial }) => serial?.sku?.id === unit.id
        ).length,
        item: {
          ...userItem.item,
          sku: unit,
          thumbnail: {
            ...userItem.item.thumbnail,
            front: {
              ...userItem.item.thumbnail.front,
              ...unit.thumbnail,
            },
          },
        },
      }))
      .filter(
        m => !!m.number
      ) as MyOwnedTicketDetailQuery['pickupUser']['userItems']
  })
}

type ItemSku = Pick<Item, 'price' | 'formattedPrice'> & {
  sku?: Maybe<Pick<SkuUnit, 'price' | 'formattedPrice'>>
}

/**
 * get item price width sku
 */
export const getItemSkuPrice = (item?: Maybe<ItemSku>) => {
  if (!isNaN(Number(item?.sku?.price ?? undefined))) {
    return {
      price: item?.sku?.price || 0,
      formattedPrice: item?.sku?.formattedPrice ?? '0',
    }
  }
  return {
    price: item?.price || 0,
    formattedPrice: item?.formattedPrice ?? '0',
  }
}

/**
 * 売り切れかどうかを判定する
 * @param item アイテム詳細
 * @param skuId SKUのID
 * @returns 売り切れの場合true
 */
export const getIsSoldOut = (
  item: Maybe<StoreItemDetailFragment> | undefined,
  skuId?: string
): boolean => {
  if (!item) return false

  const sku = item.skuSettings?.units?.find(sku => sku.id === skuId)
  if (!sku) {
    /** アイテム発行数が設定されており残数が0であれば売り切れとする */
    return !!item.issuedNumber && item.remainingNumber === 0
  }
  /** 発行数が設定されていない場合は購入可能とする */
  if (!item.issuedNumber) return false
  if (!item.isResaleAvailable) {
    /** SKUの在庫数が購入済み数と保留中購入数の合計以下であれば売り切れとする */
    return (
      (sku.stock ?? 0) <=
      (sku.purchasedNumber ?? 0) + (sku.pendingPurchaseNumber ?? 0)
    )
  }
  /** 再販利用可能時はアイテム発行数が設定されており残数が0であれば売り切れとする */
  return !!item.issuedNumber && item.remainingNumber === 0
}
